import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useTimer } from "@/context/TimerContext";
import { DeliverableBadge } from "@/components/Badges";
import { formatDuration, formatDayLabel } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Timer, MapPin, Loader2, Play, Square, ArrowRight } from "lucide-react";
import { ClientAvatar } from "@/components/ClientAvatar";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";

const DONUT_COLORS = ["#6366f1", "#ec4899", "#10b981", "#f59e0b", "#06b6d4", "#a855f7", "#ef4444", "#14b8a6", "#8b5cf6", "#f97316"];

const toISO = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISO(d);
};

const PRESETS = [
  { key: "today", label: "Today", range: () => ({ start: toISO(new Date()), end: toISO(new Date()) }) },
  { key: "7d", label: "7 days", range: () => ({ start: daysAgo(6), end: toISO(new Date()) }) },
  { key: "30d", label: "30 days", range: () => ({ start: daysAgo(29), end: toISO(new Date()) }) },
];

function TimerToggle({ entry, testid, onChanged }) {
  const { active, start, stop } = useTimer();
  const [busy, setBusy] = useState(false);
  const isActive = active &&
    String(active.deliverable_id) === String(entry.deliverable_id) &&
    String(active.order_id) === String(entry.order_id);

  const click = async (ev) => {
    ev.stopPropagation();
    if (!entry.order_id || !entry.deliverable_id) {
      toast.error("This entry can't be re-timed.");
      return;
    }
    setBusy(true);
    try {
      if (isActive) await stop();
      else await start(entry.order_id, entry.deliverable_id);
      onChanged && (await onChanged());
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button onClick={click} disabled={busy} data-testid={testid}
      title={isActive ? "Stop timer" : "Start timer"}
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors ${
        isActive
          ? "border-rose-500/50 bg-rose-500/15 text-rose-400 hover:bg-rose-500/25"
          : "border-border bg-background text-muted-foreground hover:border-primary/60 hover:bg-primary/10 hover:text-primary"
      }`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isActive ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
    </button>
  );
}

function EntryRow({ e, testid, navigate, onChanged }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg bg-secondary/40 px-3 py-2 transition-colors hover:bg-secondary/70">
      <button onClick={() => e.order_id && navigate(`/orders/${e.order_id}`)}
        data-testid={`${testid}-entry-${e.deliverable_id}`}
        className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
        <DeliverableBadge type={e.type} />
        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{e.address || e.title || "—"}</span>
          {e.client_name && (
            <>
              <span className="shrink-0 text-muted-foreground/40">·</span>
              <ClientAvatar name={e.client_name} className="h-4 w-4 text-[7px]" />
              <span className="truncate">{e.client_name}</span>
            </>
          )}
        </span>
      </button>
      <div className="flex shrink-0 items-center gap-1.5">
        {e.adjusted && (
          <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-500" data-testid={`${testid}-adjusted-${e.deliverable_id}`}>adj</span>
        )}
        <span className="font-mono text-xs font-semibold text-foreground">{formatDuration(e.seconds)}</span>
      </div>
      <TimerToggle entry={e} testid={`${testid}-timer-${e.deliverable_id}`} onChanged={onChanged} />
    </div>
  );
}

export default function TimeSummary({ testid = "time-summary", compact = false, showStats = false }) {
  const navigate = useNavigate();
  const { active } = useTimer();
  const [preset, setPreset] = useState(compact ? "30d" : "today");
  const initRange = compact ? { start: daysAgo(29), end: toISO(new Date()) } : { start: toISO(new Date()), end: toISO(new Date()) };
  const [start, setStart] = useState(initRange.start);
  const [end, setEnd] = useState(initRange.end);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(5);

  const load = useCallback(async (s, e) => {
    setLoading(true);
    try {
      const { data } = await api.get("/time-summary", { params: { start: s, end: e } });
      setData(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { setVisible(5); load(start, end); }, [start, end, load]);
  // Refresh whenever a timer starts/stops elsewhere so times stay current.
  useEffect(() => { load(start, end); }, [active, load, start, end]);

  const applyPreset = (p) => {
    setPreset(p.key);
    const r = p.range();
    setStart(r.start);
    setEnd(r.end);
  };
  const onDate = (which, val) => {
    setPreset("custom");
    if (which === "start") setStart(val);
    else setEnd(val);
  };

  const total = data?.total_seconds || 0;
  const days = data?.days || [];
  const flat = days.flatMap((day) => day.entries.map((e) => ({ ...e, day: day.day })));
  const dayTotals = Object.fromEntries(days.map((d) => [d.day, d.total_seconds]));
  const reload = () => load(start, end);

  // ---- Compact (dashboard) ----
  if (compact) {
    const recent = flat.slice(0, 5);
    return (
      <div className="rounded-xl border border-border bg-card" data-testid={testid}>
        <div className="flex items-center gap-2 border-b border-border px-5 py-4">
          <Timer className="h-4 w-4 text-primary" />
          <span className="overline text-muted-foreground">Time Tracked</span>
          <button onClick={() => navigate("/time-tracking")} data-testid={`${testid}-view-all`}
            className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:underline">
            View all <ArrowRight className="h-3 w-3" />
          </button>
        </div>
        {loading ? (
          <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : recent.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
            No time tracked yet. Start a timer on a property's product.
          </div>
        ) : (
          <div className="space-y-2 p-3">
            {recent.map((e, i) => (
              <EntryRow key={(e.deliverable_id || `${e.order_id}-${e.type}`) + "-" + i} e={e} testid={testid} navigate={navigate} onChanged={reload} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- Full (Time Tracking page) ----
  const byType = (() => {
    const m = {};
    flat.forEach((e) => { m[e.type] = (m[e.type] || 0) + (e.seconds || 0); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  })();
  const donutData = byType.map(([name, value]) => ({ name, value }));
  const donutTotal = byType.reduce((s, [, v]) => s + v, 0) || 1;

  return (
    <div className="space-y-6">
      {showStats && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="flex flex-col gap-4">
            {[
              { label: "Total tracked", value: formatDuration(total), tint: "#6366f1" },
              { label: "Active days", value: days.length, tint: "#10b981" },
            ].map((c) => (
              <div key={c.label} className="rounded-xl border border-border bg-card p-5" data-testid={`${testid}-stat-${c.label.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <span className="overline text-muted-foreground">{c.label}</span>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${c.tint}22` }}>
                    <Timer className="h-4 w-4" style={{ color: c.tint }} />
                  </div>
                </div>
                <div className="mt-3 font-heading text-3xl font-extrabold tracking-tight">{c.value}</div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2" data-testid={`${testid}-donut`}>
            <span className="overline text-muted-foreground">Time by product</span>
            {donutData.length === 0 ? (
              <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">No time tracked yet</div>
            ) : (
              <div className="mt-2 flex flex-col items-center gap-6 sm:flex-row">
                <div className="h-[220px] w-[220px] shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={62} outerRadius={95} paddingAngle={2} stroke="none">
                        {donutData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [formatDuration(value), name]}
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                        itemStyle={{ color: "hsl(var(--foreground))" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-full flex-1 space-y-2">
                  {donutData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-2.5 text-sm" data-testid={`${testid}-donut-legend-${d.name.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                      <span className="min-w-0 flex-1 truncate font-medium">{d.name}</span>
                      <span className="shrink-0 font-mono text-xs text-muted-foreground">{formatDuration(d.value)}</span>
                      <span className="w-10 shrink-0 text-right font-mono text-xs text-muted-foreground">{Math.round((d.value / donutTotal) * 100)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card" data-testid={testid}>
        <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2">
            <Timer className="h-4 w-4 text-primary" />
            <span className="overline text-muted-foreground">Time Tracked</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <div className="flex rounded-lg border border-border p-0.5">
              {PRESETS.map((p) => (
                <button key={p.key} onClick={() => applyPreset(p)} data-testid={`${testid}-preset-${p.key}`}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${preset === p.key ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <Input type="date" value={start} max={end} onChange={(e) => onDate("start", e.target.value)}
              className="h-8 w-[140px] text-xs" data-testid={`${testid}-start`} />
            <span className="text-xs text-muted-foreground">→</span>
            <Input type="date" value={end} min={start} max={toISO(new Date())} onChange={(e) => onDate("end", e.target.value)}
              className="h-8 w-[140px] text-xs" data-testid={`${testid}-end`} />
          </div>
        </div>

        <div className="flex items-center justify-between px-5 py-3">
          <span className="text-xs text-muted-foreground">{days.length} day{days.length === 1 ? "" : "s"} with activity</span>
          <span className="font-mono text-sm font-semibold text-foreground" data-testid={`${testid}-total`}>{formatDuration(total)}</span>
        </div>

        {loading ? (
          <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : days.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
            No time tracked in this range. Start a timer on a property's product to log work here.
          </div>
        ) : (() => {
          const shown = flat.slice(0, visible);
          let lastDay = null;
          return (
            <>
              <div className="max-h-[520px] divide-y divide-border overflow-y-auto">
                {shown.map((e, i) => {
                  const showHeader = e.day !== lastDay;
                  lastDay = e.day;
                  return (
                    <div key={(e.deliverable_id || `${e.order_id}-${e.type}`) + "-" + i} className="px-5 py-2">
                      {showHeader && (
                        <div className="mb-1.5 mt-1 flex items-center justify-between">
                          <span className="text-sm font-medium">{formatDayLabel(e.day, { weekday: "short", month: "short", day: "numeric" })}</span>
                          <span className="font-mono text-xs font-semibold text-muted-foreground" data-testid={`${testid}-day-total-${e.day}`}>{formatDuration(dayTotals[e.day] || 0)}</span>
                        </div>
                      )}
                      <EntryRow e={e} testid={testid} navigate={navigate} onChanged={reload} />
                    </div>
                  );
                })}
              </div>
              {flat.length > visible && (
                <button onClick={() => setVisible((v) => v + 10)} data-testid={`${testid}-show-more`}
                  className="w-full border-t border-border py-2.5 text-xs font-medium text-primary transition-colors hover:bg-secondary/50">
                  Show more ({flat.length - visible} left)
                </button>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}
